// src/Components/ReleaseModal.js
import React, { useEffect, useState } from 'react';
import Resizer from 'react-image-file-resizer';
import crossIcon from '../assets/icons/cross.svg';
import { api } from '../api';

/* ── reusable input style ─────────────────────────────── */
const modalInput = {
  width: '100%',
  padding: 10,
  background: '#333',
  color: '#fff',
  border: 'none',
  borderRadius: 5,
  fontFamily: 'inherit',
  marginBottom: 10,
  boxSizing: 'border-box',
};

/* colour chips for release types */
const TYPE_COLORS = {
  Single: '#444444',          // dark grey
  'Focus Single': '#f3b40b',  // golden-yellow
  'Impact Date': '#444444',   // keep grey
  'Re-Release': '#78be1e',    // fresh green
};

/* colour chips for release status */
const STATUS_COLORS = {
  Confirmed: '#28a745', // green
  Hold: '#f39c12',      // orange
};

export default function ReleaseModal({
  open,
  initialRelease = null,
  projectContextId = null,
  defaultReleaseDate = '',
  onSave,
  onCancel,
  onDelete,
  allArtists = [],
  allProjects = [],
  onArtistsRefresh,              // 👈 NEW (optional)
}) {
  /* ──────────────────────────────────────────────────────
     1. Local draft state
     ─────────────────────────────────────────────────── */
  const blank = {
    title: '',
    releaseDate: '',
    coverArt: '',
    artists: [],                 // store IDs
    projectId: projectContextId,
    isArchived: false,
    releaseType: 'Single',
    releaseStatus: '',  // ← NEW: default status
  };
  const [draft, setDraft] = useState(blank);

  /* ──────────────────────────────────────────────────────
     2. UI helper state
     ─────────────────────────────────────────────────── */
  const [artistSearch, setArtistSearch] = useState('');
  const [showArtistList, setShowArtistList] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectList, setShowProjectList] = useState(false);
  const [coverPreview, setCoverPreview] = useState('');
  const [saving, setSaving] = useState(false);      // ← guard against double-click
  const [artistsIndex, setArtistsIndex] = useState(allArtists);
  useEffect(() => setArtistsIndex(allArtists), [allArtists]);
  // inline “create artist” panel state
const [artistCreateOpen, setArtistCreateOpen] = useState(false);
const [artistCreateEmails, setArtistCreateEmails] = useState(['']);

  /* small helpers */
  const toId = (val) => (val && typeof val === 'object' ? val._id : val);
  const sameId = (a, b) => String(a) === String(b);

  /* ──────────────────────────────────────────────────────
     3. Initialise when modal opens
     ─────────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;

    if (initialRelease) {
      /* edit mode — normalize artists to IDs */
      const normalizedArtists = Array.isArray(initialRelease.artists)
        ? initialRelease.artists.map(toId)
        : [];

      setDraft({
        title: initialRelease.title || '',
        releaseDate: initialRelease.releaseDate
          ? initialRelease.releaseDate.slice(0, 10)
          : '',
        coverArt: initialRelease.coverArt || '',
        artists: normalizedArtists,                 // IDs only
        projectId: toId(initialRelease.projectId) || projectContextId || null,
        isArchived: Boolean(initialRelease.isArchived),
        releaseType: initialRelease.releaseType || 'Single',
        releaseStatus: initialRelease.releaseStatus || 'Confirmed', // ← NEW
      });
      setCoverPreview(initialRelease.coverArt || '');

      const projTitle =
        initialRelease.projectId &&
        allProjects.find((p) => sameId(p._id, toId(initialRelease.projectId)))?.title;
      setProjectSearch(projTitle || '');
      setArtistCreateOpen(false);
setArtistCreateEmails(['']);
    } else {
      /* add mode — optionally pre-fill from project context */
      const projectObj =
        projectContextId &&
        allProjects.find((p) => sameId(p._id, projectContextId));

      const autoArtistIds = Array.isArray(projectObj?.artists)
        ? projectObj.artists.map(toId)
        : [];

      setDraft({
        ...blank,
        projectId: projectContextId || null,
        artists: autoArtistIds,
        releaseDate: defaultReleaseDate || '',
      });

      setCoverPreview('');
      setProjectSearch(projectObj ? projectObj.title : '');
      setArtistCreateOpen(false);
setArtistCreateEmails(['']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ──────────────────────────────────────────────────────
     4. Helpers
     ─────────────────────────────────────────────────── */
  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    Resizer.imageFileResizer(
      f,
      600,
      600,
      'JPEG',
      80,
      0,
      (uri) => {
        setDraft((d) => ({ ...d, coverArt: uri }));
        setCoverPreview(uri);
      },
      'base64'
    );

    
  };
// inline-create artist email helpers
const addArtistEmailRow    = () => setArtistCreateEmails(prev => [...prev, '']);
const removeArtistEmailRow = (idx) => setArtistCreateEmails(prev => prev.filter((_, i) => i !== idx));
const updateArtistEmailRow = (idx, val) =>
  setArtistCreateEmails(prev => prev.map((e, i) => (i === idx ? val : e)));

const normalizeEmails = (list) => {
  const seen = new Set();
  const out = [];
  for (const e of list || []) {
    const v = String(e || '').trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (!seen.has(key)) { seen.add(key); out.push(v); }
  }
  return out;
};

// Create a new artist in DB, append to the local list, and select it
const createNewArtist = async (name) => {
  const clean = String(name || '').trim();
  if (!clean) { alert('Please type a name first.'); return; }

  const emails = normalizeEmails(artistCreateEmails);

  try {
    const res = await api('/api/artists', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: clean, emails }),
    });
    if (!res.ok) throw new Error(`Failed to create artist (${res.status})`);
    const newArtist = await res.json();

    // extend local list so dropdown & chips see the new one
    setArtistsIndex(prev => [newArtist, ...(prev || [])]);

    // select immediately
    addArtist(newArtist);

    // ask parent to refresh its global artists list (so the next modal sees it)
if (typeof onArtistsRefresh === 'function') {
  try { await onArtistsRefresh(); } catch (_) {}
}

    // clear UI
    setArtistSearch('');
    setShowArtistList(false);
    setArtistCreateOpen(false);
    setArtistCreateEmails(['']);
  } catch (err) {
    alert(err.message || 'Failed to create artist');
  }
};

 const addArtist = (artObj) => {
  const id = toId(artObj);
  if (draft.artists.map(toId).some((x) => sameId(x, id))) return;
  setDraft((d) => ({ ...d, artists: [...d.artists.map(toId), id] }));
  setArtistSearch('');
  setShowArtistList(false);
  setArtistCreateOpen(false);    // 👈 close inline-create panel
  setArtistCreateEmails(['']);   // 👈 reset email rows
};

  

  const removeArtist = (idOrObj) => {
    const id = toId(idOrObj);
    setDraft((d) => ({ ...d, artists: d.artists.map(toId).filter((x) => !sameId(x, id)) }));
  };

  

const filteredArtist = (artistsIndex || []).filter(
  (a) =>
    (a?.name || '').toLowerCase().includes(artistSearch.toLowerCase()) &&
    !draft.artists.map(toId).some((x) => sameId(x, a._id))
);

const selectProject = (proj) => {
  setDraft((d) => ({
    ...d,
    projectId: toId(proj._id),
    artists: Array.isArray(proj.artists) ? proj.artists.map(toId) : d.artists.map(toId),
  }));
  setProjectSearch(proj.title);
  setShowProjectList(false);

  // also close/reset artist inline-create UI if it was open
  setArtistCreateOpen(false);
  setArtistCreateEmails(['']);
};

  const handleSaveClick = async () => {
    if (saving) return;                 // guard double-clicks
    setSaving(true);
    try {
      const payload = {
        ...draft,
        artists: draft.artists.map(toId), // ensure IDs
      };
      await onSave(payload);             // parent handles API + closing modal
    } finally {
      setSaving(false);
    }
  };

  

  /* ──────────────────────────────────────────────────────
     5. Early return
     ─────────────────────────────────────────────────── */
  if (!open) return null;

  /* ──────────────────────────────────────────────────────
     6. Render (scrollable like ProjectModal)
     ─────────────────────────────────────────────────── */
  const isEdit = Boolean(initialRelease);

  return (
    <div
      className="modal"
      style={{
        position: 'fixed',
        inset: 0, // top/right/bottom/left: 0
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start', // keep header visible on small screens
        overflowY: 'auto', // overlay scroll
        padding: '24px',
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        style={{
          width: 600,
          maxWidth: '90vw',
          background: '#1e1e1e',
          color: '#fff',
          borderRadius: 10,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          boxSizing: 'border-box',
          maxHeight: 'calc(100vh - 48px)', // modal capped to viewport
          overflowY: 'auto', // modal content scrolls
          margin: '0 auto',
          position: 'relative',
        }}
      >
        <h2 style={{ margin: '0 0 10px 0' }}>
          {isEdit ? 'Edit Release' : 'Add New Release'}
        </h2>

        {/* ─── Song Title ─────────────────────────── */}
        <label>Song Title:</label>
        <input
          style={modalInput}
          placeholder="Song Title…"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          disabled={saving}
        />

        {/* ─── Release Type chips ─────────────────── */}
        <label>Release Type:</label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 5 }}>
          {['Single', 'Focus Single', 'Impact Date', 'Re-Release'].map((t) => {
            const sel = draft.releaseType === t;
            return (
              <div
                key={t}
                onClick={() => !saving && setDraft({ ...draft, releaseType: t })}
                style={{
                  padding: '6px 10px',
                  borderRadius: 5,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  background: sel ? TYPE_COLORS[t] : '#2c2c2c',
                  fontWeight: 'bold',
                  fontSize: 13,
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {t}
              </div>
            );
          })}
        </div>

        {/* ─── Project search ─────────────────────── */}
        <label>Link to Project:</label>
        <div style={{ position: 'relative' }}>
          <input
            style={modalInput}
            placeholder="Search project…"
            value={projectSearch}
            onChange={(e) => {
              setProjectSearch(e.target.value);
              setShowProjectList(true);
              if (draft.projectId) setDraft((d) => ({ ...d, projectId: null }));
            }}
            onFocus={() => setShowProjectList(projectSearch.trim().length > 0)}
            disabled={saving}
          />
          {showProjectList && projectSearch.trim() && !saving && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                width: '100%',
                background: '#1e1e1e',
                border: '1px solid #555',
                borderRadius: 5,
                maxHeight: 150,
                overflowY: 'auto',
                zIndex: 999,
              }}
            >
              {allProjects
                .filter((p) => p.title.toLowerCase().includes(projectSearch.toLowerCase()))
                .map((p) => (
                  <div
                    key={p._id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectProject(p)}
                    style={{
                      padding: 10,
                      cursor: 'pointer',
                      borderBottom: '1px solid #333',
                    }}
                  >
                    {p.title}
                  </div>
                ))}
              {!allProjects.length && (
                <div style={{ padding: 10, color: '#999' }}>No matching projects</div>
              )}
            </div>
          )}
        </div>

        {/* ─── Artist search ──────────────────────── */}
        <label>Artists:</label>
        <div style={{ position: 'relative' }}>
          <input
            style={modalInput}
            placeholder="Search artist…"
            value={artistSearch}
            onChange={(e) => {
              setArtistSearch(e.target.value);
              setShowArtistList(true);
            }}
            onFocus={() => setShowArtistList(artistSearch.trim().length > 0)}
            disabled={saving}
          />
          {showArtistList && artistSearch.trim() && !saving && (
  <div
    style={{
      position: 'absolute',
      top: '100%',
      left: 0,
      width: '100%',
      background: '#1e1e1e',
      border: '1px solid #555',
      borderRadius: 5,
      maxHeight: 220,
      overflowY: 'auto',
      zIndex: 999,
    }}
  >
    {filteredArtist.length ? (
      filteredArtist.map((a) => (
        <div
          key={a._id}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => addArtist(a)}
          style={{ padding: 10, cursor: 'pointer', borderBottom: '1px solid #333' }}
        >
          {a.name}
        </div>
      ))
    ) : (
      <div style={{ padding: 10, color: '#999' }}>No matching artists</div>
    )}

    {/* Toggle inline create */}
    {!artistCreateOpen ? (
      <div
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setArtistCreateOpen(true)}
        style={{ padding: 10, cursor: 'pointer', color: '#28a745', fontWeight: 'bold' }}
      >
        + Create “{artistSearch}” with emails
      </div>
    ) : (
      <div style={{ padding: 10, borderTop: '1px solid #333' }}>
        <div style={{ color: '#bbb', fontSize: 12, marginBottom: 6 }}>
          New artist: <strong style={{ color: '#fff' }}>{artistSearch}</strong>
        </div>

        {/* emails list */}
        {artistCreateEmails.map((val, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input
              type="email"
              placeholder="name@domain.com"
              value={val}
              onChange={(e) => updateArtistEmailRow(idx, e.target.value)}
              style={{
                flex: 1,
                padding: 8,
                background: '#2a2a2a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
              }}
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => removeArtistEmailRow(idx)}
              style={{
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '6px 10px',
                cursor: 'pointer',
              }}
              title="Remove"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={addArtistEmailRow}
          style={{
            padding: '6px 10px',
            background: '#555',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          + Add email
        </button>

        {/* actions */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => createNewArtist(artistSearch)}
            style={{
              padding: '6px 10px',
              background: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            Create & Add
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setArtistCreateOpen(false); setArtistCreateEmails(['']); }}
            style={{
              padding: '6px 10px',
              background: '#555',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
  </div>
)}
        </div>

        {/* selected artist chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {draft.artists.map((val) => {
            const id = toId(val);
            const art = (artistsIndex || []).find((a) => sameId(a._id, id));
            return (
              <div
                key={id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '5px 8px',
                  background: '#171717',
                  borderRadius: 5,
                  opacity: saving ? 0.7 : 1,
                }}
              >
                <span>{art?.name || 'Unknown'}</span>
                <img
                  src={crossIcon}
                  alt="x"
                  onClick={() => !saving && removeArtist(id)}
                  style={{
                    width: 14,
                    height: 14,
                    marginLeft: 8,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* ─── Release date + Status (same line) ───────────────────────── */}
        <label>Release Date & Status:</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <input
            type="date"
            style={{ ...modalInput, marginBottom: 0 }}
            value={draft.releaseDate}
            onChange={(e) => setDraft({ ...draft, releaseDate: e.target.value })}
            disabled={saving}
          />

          {/* Status chips */}
          {['Confirmed', 'Hold'].map((s) => {
            const sel = draft.releaseStatus === s;
            return (
              <div
                key={s}
                onClick={() => !saving && setDraft({ ...draft, releaseStatus: s })}
                style={{
                  padding: '6px 10px',
                  borderRadius: 5,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  background: sel ? STATUS_COLORS[s] : '#2c2c2c',
                  fontWeight: 'bold',
                  fontSize: 13,
                  color: '#fff',
                  opacity: saving ? 0.7 : 1,
                  
                }}
                title={`Set status to ${s}`}
              >
                {s}
              </div>
            );
          })}
        </div>

        {/* ─── Cover art ──────────────────────────── */}
        <label>Cover Art:</label>
        <input type="file" style={modalInput} onChange={handleFile} disabled={saving} />
        {coverPreview && (
          <img src={coverPreview} alt="preview" style={{ width: 60, height: 60, marginBottom: 10 }} />
        )}

        {/* ─── buttons ───────────────────────────── */}
        <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center' }}>
          <button
            onClick={handleSaveClick}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: '#28a745',
              border: 'none',
              color: '#fff',
              borderRadius: 5,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>

          <button
            onClick={onCancel}
            disabled={saving}
            style={{
              padding: '10px 20px',
              background: '#555',
              border: 'none',
              color: '#fff',
              borderRadius: 5,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.8 : 1,
            }}
          >
            Cancel
          </button>

          {/* Delete (only in edit mode, when onDelete provided) */}
          {isEdit && typeof onDelete === 'function' && (
            <button
              onClick={onDelete}
              disabled={saving}
              style={{
                padding: '10px 20px',
                background: '#ea5a54',
                border: 'none',
                color: '#fff',
                borderRadius: 5,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.8 : 1,
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ display: 'inline-block', fontWeight: 'bold' }}>×</span>
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}